import { Link } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { site } from "@/config/site";
import { BrandLogo } from "@/features/layout/components/BrandLogo";
import { ExternalLinkButton } from "@/pages/auth/ExternalLinkButton";
import { cn } from "@/utils/cn";

const STEPS = [
  { title: "Install on Marketplace", body: "Find InvoiceVault and install." },
  { title: "Open Dashboard", body: "InvoiceVault appears in the right-hand drawer." },
  { title: "Click any app link", body: "We sign you in automatically." },
] as const;

export function WelcomePage() {
  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg)]">
      <header className="flex items-center justify-between border-b border-[var(--border-soft)] bg-[var(--surface)] px-6 py-4">
        <BrandLogo />
        <nav className="flex items-center gap-5 text-sm font-medium">
          <Link to={ROUTES.help} className="hidden text-[var(--text-secondary)] hover:text-[var(--accent)] sm:inline">
            Help
          </Link>
          <a
            href={site.stripeDashboardUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[var(--text-secondary)] hover:text-[var(--accent)]"
          >
            Stripe Dashboard ↗
          </a>
        </nav>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="w-full max-w-[640px] text-center">
          <BrandLogo showName={false} className="mx-auto mb-5 justify-center" iconClassName="h-14 w-14" />

          <h1 className="text-[1.625rem] font-bold tracking-tight text-[var(--text)]">
            Open from Stripe Dashboard
          </h1>
          <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-[var(--text-secondary)]">
            Export invoice PDFs from Stripe. No password — sign in via the Dashboard app.
          </p>

          <div className="mx-auto mt-6 grid max-w-[36rem] grid-cols-1 gap-3 text-left sm:grid-cols-2">
            <div className="flex flex-col rounded-[var(--radius)] border border-teal-200 bg-gradient-to-b from-teal-50/80 to-white p-4 shadow-sm">
              <h2 className="text-[0.9375rem] font-bold text-[var(--accent)]">Already installed?</h2>
              <p className="mt-1 flex-1 text-[0.8125rem] leading-snug text-[var(--text-secondary)]">
                Open InvoiceVault in Dashboard. Any app link signs you in.
              </p>
              <div className="mt-4">
                <ExternalLinkButton href={site.stripeDashboardUrl}>Open Stripe Dashboard</ExternalLinkButton>
              </div>
            </div>
            <div className="flex flex-col rounded-[var(--radius)] border border-[var(--border)] bg-white p-4 shadow-sm">
              <h2 className="text-[0.9375rem] font-bold text-[var(--text)]">First time here?</h2>
              <p className="mt-1 flex-1 text-[0.8125rem] leading-snug text-[var(--text-secondary)]">
                Install on Marketplace first — then it shows in Dashboard.
              </p>
              <div className="mt-4">
                <ExternalLinkButton href={site.stripeMarketplaceUrl} variant="secondary">
                  Install on Marketplace
                </ExternalLinkButton>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-6 max-w-md rounded-[var(--radius)] border border-[var(--border)] bg-white px-5 py-5 text-left shadow-sm">
            <p className="mb-4 text-xs font-bold uppercase tracking-wider text-[var(--muted)]">How it works</p>
            <ol className="space-y-0">
              {STEPS.map((step, index) => (
                <li
                  key={step.title}
                  className={cn(
                    "flex gap-3.5",
                    index > 0 && "mt-4 border-t border-[var(--border-soft)] pt-4",
                  )}
                >
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--accent-light)] text-xs font-bold text-[var(--accent)]">
                    {index + 1}
                  </span>
                  <div>
                    <strong className="block text-[0.9375rem] font-semibold text-[var(--text)]">{step.title}</strong>
                    <span className="mt-0.5 block text-sm text-[var(--text-secondary)]">{step.body}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <nav className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm">
            <Link to={ROUTES.help} className="font-semibold text-[var(--accent)]">
              Help &amp; Support
            </Link>
            <Link to={ROUTES.privacy} className="font-medium text-[var(--muted)] hover:text-[var(--text-secondary)]">
              Privacy
            </Link>
            <Link to={ROUTES.terms} className="font-medium text-[var(--muted)] hover:text-[var(--text-secondary)]">
              Terms
            </Link>
          </nav>
        </div>
      </main>

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-soft)] bg-[var(--surface)] px-6 py-4 text-[0.8125rem] text-[var(--muted)]">
        <span>© {site.brandName} · {site.name}</span>
        <nav>
          <a href={`mailto:${site.supportEmail}`} className="hover:text-[var(--text-secondary)]">
            {site.supportEmail}
          </a>
        </nav>
      </footer>
    </div>
  );
}
